/**
 * Modern Sidebar Component
 * Collapsible navigation with icons and smooth transitions
 */

import React, { useState } from 'react';
import { Box, Drawer, List, Typography, IconButton, Divider, Avatar, Tooltip, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import PeopleOutlinedIcon from '@mui/icons-material/PeopleOutlined';
import EventNoteOutlinedIcon from '@mui/icons-material/EventNoteOutlined';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import TimelineOutlinedIcon from '@mui/icons-material/TimelineOutlined';
import LinkOffOutlinedIcon from '@mui/icons-material/LinkOffOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import ReceiptOutlinedIcon from '@mui/icons-material/ReceiptOutlined';
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import GroupAddOutlinedIcon from '@mui/icons-material/GroupAddOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import SidebarNavItem from './SidebarNavItem';
import { setShowList } from '../../../redux/modules/showList/slice';
import { USER_ROLE } from '../../../constants/Constant';
import { usePermissions } from '../../../hooks/usePermissions';
import { isFeatureEnabled } from '../../../config/features';
import features from '../../../config/features';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined';

import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import SummarizeOutlinedIcon from '@mui/icons-material/SummarizeOutlined';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';

// Sidebar widths
const DRAWER_WIDTH_EXPANDED = 260;
const DRAWER_WIDTH_COLLAPSED = 72;

// Styled components
const DrawerHeader = styled(Box)(({ theme, collapsed }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: collapsed ? 'center' : 'space-between',
  padding: collapsed ? theme.spacing(2) : theme.spacing(2, 3),
  minHeight: 64,
}));

const LogoContainer = styled(Box)(({ theme, collapsed }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  overflow: 'hidden',
  transition: 'all 200ms ease',
}));

const DrawerFooter = styled(Box)(({ theme, collapsed }) => ({
  marginTop: 'auto',
  padding: collapsed ? theme.spacing(2, 1) : theme.spacing(2, 2),
  borderTop: `1px solid ${theme.palette.divider}`,
}));

const UserSection = styled(Box)(({ theme, collapsed }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  padding: collapsed ? theme.spacing(1) : theme.spacing(1.5),
  borderRadius: theme.shape.borderRadius,
  cursor: 'pointer',
  transition: 'all 150ms ease',
  justifyContent: collapsed ? 'center' : 'flex-start',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

const StyledDrawer = styled(Drawer)(({ theme, collapsed }) => ({
  width: collapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH_EXPANDED,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  '& .MuiDrawer-paper': {
    width: collapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH_EXPANDED,
    boxSizing: 'border-box',
    backgroundColor: theme.palette.background.paper,
    borderRight: `1px solid ${theme.palette.divider}`,
    transition: 'width 200ms ease',
    overflowX: 'hidden',
  },
}));

// Build the nav from the user's CAPABILITIES (perm.canAccess / perm.can),
// intersected with the hospital's enabled modules and feature flags. A section
// shows only if: the user can read its capability AND the owning module is
// enabled for this hospital AND its feature flag is on. Admin (super-admin) is
// granted every capability by usePermissions, so admins see everything enabled.
const getNavItems = (basePath, mode, modules, perm, hospitalFeatures = {}) => {
  const lisOn = !!(modules?.lis?.enabled ?? modules?.lis);
  const risOn = !!(modules?.ris?.enabled ?? modules?.ris);
  const emrOn = !!(modules?.emr?.enabled ?? modules?.emr);

  const isPatient = basePath === '/Patient';

  // Patient portal — patients have no capability grid; gate by enabled modules.
  if (isPatient) {
    return [
      { to: basePath, label: 'Dashboard', icon: <DashboardOutlinedIcon />, exact: true, visible: true },
      { to: `${basePath}/my-reports`, label: 'My Reports', icon: <DescriptionOutlinedIcon />, visible: risOn },
      { to: `${basePath}/my-lab-results`, label: 'My Lab Results', icon: <ScienceOutlinedIcon />, visible: lisOn },
    ].filter(item => item.visible);
  }

  const { canAccess, can } = perm;
  // The Settings area aggregates several admin/config sub-pages — show it if the
  // user can reach any of them.
  const settingsVisible = canAccess('settings') || canAccess('labSettings') || canAccess('labInventory');

  // Laboratory mode navigation
  if (mode === 'laboratory') {
    const items = [
      { to: `${basePath}/laboratory`, label: 'Dashboard', icon: <DashboardOutlinedIcon />, exact: true, visible: canAccess('dashboard'), moduleId: 'lis' },
      { to: `${basePath}/patient-management`, label: 'Patients', icon: <PeopleOutlinedIcon />, visible: canAccess('patients') && isFeatureEnabled('core.patientManagement'), moduleId: 'emr' },
      { to: `${basePath}/appointment`, label: 'Appointments', icon: <EventNoteOutlinedIcon />, visible: canAccess('appointments') && isFeatureEnabled('scheduling.enabled') },
      { to: `${basePath}/laboratory/orders`, label: 'Lab Orders', icon: <AssignmentOutlinedIcon />, visible: canAccess('labOrders'), moduleId: 'lis' },
      { to: `${basePath}/laboratory/specimens`, label: 'Specimens', icon: <ScienceOutlinedIcon />, visible: canAccess('labOrders'), moduleId: 'lis' },
      { to: `${basePath}/laboratory/results`, label: 'Results', icon: <EditNoteOutlinedIcon />, visible: canAccess('labResults'), moduleId: 'lis' },
      { to: `${basePath}/laboratory/results/authorization`, label: 'Authorization', icon: <VerifiedOutlinedIcon />, visible: (can('labResultValidation:write') || can('labResultAuth:write')) && hospitalFeatures['lis.authorizationQueue'] !== false, moduleId: 'lis' },
      { to: `${basePath}/laboratory/unmatched-results`, label: 'Unmatched Results', icon: <LinkOffOutlinedIcon />, visible: canAccess('labResults'), moduleId: 'lis' },
      { to: `${basePath}/laboratory/reports`, label: 'Lab Reports', icon: <SummarizeOutlinedIcon />, visible: canAccess('labReports'), moduleId: 'lis' },
      { to: `${basePath}/laboratory/qc`, label: 'QC', icon: <FactCheckOutlinedIcon />, visible: canAccess('labQC'), moduleId: 'lis' },
      { to: `${basePath}/laboratory/analytics`, label: 'Lab Analytics', icon: <InsightsOutlinedIcon />, visible: canAccess('labAnalytics'), moduleId: 'lis' },
      { to: `${basePath}/billing-invoice`, label: 'Billing', icon: <ReceiptOutlinedIcon />, visible: canAccess('billing') && isFeatureEnabled('billing.enabled') },
      { to: `${basePath}/users`, label: 'Users', icon: <PersonOutlinedIcon />, visible: canAccess('users') },
      { to: `${basePath}/patient-accounts`, label: 'Patient Accounts', icon: <AccountCircleOutlinedIcon />, visible: canAccess('patientAccounts'), moduleId: 'emr' },
      { to: `${basePath}/settings`, label: 'Settings', icon: <SettingsOutlinedIcon />, visible: settingsVisible },
    ];
    return items.filter(item => item.visible && (!item.moduleId || (item.moduleId === 'lis' && lisOn) || (item.moduleId === 'ris' && risOn) || (item.moduleId === 'emr' && emrOn)));
  }

  // Radiology mode navigation (default)
  const items = [
    { to: basePath, label: 'Dashboard', icon: <DashboardOutlinedIcon />, exact: true, visible: canAccess('dashboard') },
    { to: `${basePath}/patient-management`, label: 'Patients', icon: <PeopleOutlinedIcon />, visible: canAccess('patients') && isFeatureEnabled('core.patientManagement'), moduleId: 'emr' },
    { to: `${basePath}/appointment`, label: 'Appointments', icon: <EventNoteOutlinedIcon />, visible: canAccess('appointments') && isFeatureEnabled('scheduling.enabled') },
    { to: `${basePath}/radiology`, label: 'Study Orders', icon: <AssignmentOutlinedIcon />, visible: canAccess('studyOrders') && isFeatureEnabled('imaging.enabled'), moduleId: 'ris' },
    { to: `${basePath}/unlinked-studies`, label: 'Unlinked Studies', icon: <LinkOffOutlinedIcon />, visible: canAccess('unlinkedStudies') && isFeatureEnabled('imaging.enabled'), moduleId: 'ris' },
    { to: `${basePath}/image-report`, label: 'Imaging', icon: <ImageOutlinedIcon />, visible: canAccess('imaging') && isFeatureEnabled('imaging.reporting'), moduleId: 'ris' },
    { to: `${basePath}/anlytics-reporting`, label: 'Analytics', icon: <BarChartOutlinedIcon />, visible: canAccess('analytics') && isFeatureEnabled('analytics.enabled') },
    { to: `${basePath}/billing-invoice`, label: 'Billing', icon: <ReceiptOutlinedIcon />, visible: canAccess('billing') && isFeatureEnabled('billing.enabled') },
    { to: `${basePath}/users`, label: 'Users', icon: <PersonOutlinedIcon />, visible: canAccess('users') },
    { to: `${basePath}/patient-accounts`, label: 'Patient Accounts', icon: <AccountCircleOutlinedIcon />, visible: canAccess('patientAccounts'), moduleId: 'emr' },
    { to: `${basePath}/external-referrers`, label: 'External Access', icon: <GroupAddOutlinedIcon />, visible: canAccess('externalAccess'), moduleId: 'ris' },
    { to: `${basePath}/settings`, label: 'Settings', icon: <SettingsOutlinedIcon />, visible: settingsVisible },
  ];

  return items.filter(item => item.visible && (!item.moduleId || (item.moduleId === 'lis' && lisOn) || (item.moduleId === 'ris' && risOn) || (item.moduleId === 'emr' && emrOn)));
};

const Sidebar = ({
  basePath = '/admin',
  isCollapsed = false,
  mobileOpen = false,
  onToggleCollapse,
  onCloseMobile,
  appMode = 'laboratory',
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { userData } = useSelector(state => state.auth);
  const hospitalModules = useSelector(state => state.hospitalConfig?.modules);
  const hospitalFeatures = useSelector(state => state.hospitalConfig?.features);
  const perm = usePermissions();
  const navItems = getNavItems(basePath, appMode, hospitalModules, perm, hospitalFeatures);
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);

  const handleNavClick = () => {
    dispatch(setShowList(true));
    if (mobileOpen) {
      onCloseMobile?.();
    }
  };

  const handleLogoutClick = () => {
    setLogoutDialogOpen(true);
  };

  const handleLogoutConfirm = () => {
    setLogoutDialogOpen(false);
    localStorage.clear();
    navigate('/login');
  };

  const handleLogoutCancel = () => {
    setLogoutDialogOpen(false);
  };

  const getUserInitials = () => {
    if (userData?.name) {
      return userData.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    }
    return 'U';
  };

  // Drawer content
  const drawerContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header with Logo */}
      <DrawerHeader collapsed={isCollapsed ? 1 : 0}>
        <LogoContainer collapsed={isCollapsed ? 1 : 0}>
          <Box
            component="img"
            src={new URL('../../../assets/images/main-logo.png', import.meta.url).href}
            alt="Logo"
            sx={{
              height: 36,
              width: 'auto',
              objectFit: 'contain',
            }}
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
          {!isCollapsed && (
            <Box>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 600, color: 'primary.dark', lineHeight: 1.2 }}
              >
                {features.client?.shortName || 'PACS'}
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: 'text.secondary', fontSize: '0.7rem' }}
              >
                {features.client?.subtitle || 'Medical Information System'}
              </Typography>
            </Box>
          )}
        </LogoContainer>
        {!isCollapsed && onToggleCollapse && (
          <IconButton
            onClick={onToggleCollapse}
            size="small"
            sx={{
              color: 'text.secondary',
              '&:hover': { color: 'primary.main' },
            }}
          >
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
        )}
      </DrawerHeader>

      {/* Collapsed toggle button */}
      {isCollapsed && onToggleCollapse && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
          <IconButton
            onClick={onToggleCollapse}
            size="small"
            sx={{
              color: 'text.secondary',
              '&:hover': { color: 'primary.main' },
            }}
          >
            <ChevronRightIcon fontSize="small" />
          </IconButton>
        </Box>
      )}

      <Divider />

      {/* Navigation Items */}
      <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', py: 2, px: isCollapsed ? 1 : 2 }}>
        <List disablePadding>
          {navItems.map((item) => (
            <SidebarNavItem
              key={item.to}
              to={item.to}
              label={item.label}
              icon={item.icon}
              collapsed={isCollapsed}
              exact={item.exact}
              onClick={handleNavClick}
            />
          ))}
        </List>
      </Box>

      {/* Footer with User */}
      <DrawerFooter collapsed={isCollapsed ? 1 : 0}>
        <Tooltip title={isCollapsed ? 'Logout' : ''} placement="right">
          <UserSection
            collapsed={isCollapsed ? 1 : 0}
            onClick={handleLogoutClick}
          >
            <Avatar
              sx={{
                width: 36,
                height: 36,
                bgcolor: 'primary.main',
                fontSize: '0.875rem',
              }}
            >
              {getUserInitials()}
            </Avatar>
            {!isCollapsed && (
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    color: 'text.primary',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {userData?.name || 'User'}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: 'text.secondary' }}
                >
                  {USER_ROLE.find(r => r.role === userData?.role)?.displayText || userData?.role || 'Staff'}
                </Typography>
              </Box>
            )}
            {!isCollapsed && (
              <LogoutOutlinedIcon
                sx={{ color: 'text.secondary', fontSize: 20 }}
              />
            )}
          </UserSection>
        </Tooltip>
      </DrawerFooter>

      {/* Logout Confirmation Dialog */}
      <Dialog
        open={logoutDialogOpen}
        onClose={handleLogoutCancel}
        PaperProps={{
          sx: {
            borderRadius: 3,
            minWidth: 320,
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 600 }}>Confirm Logout</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to log out of your account?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleLogoutCancel}
            sx={{ borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleLogoutConfirm}
            variant="contained"
            color="error"
            sx={{ borderRadius: 2 }}
          >
            Logout
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );

  return (
    <Box
      component="nav"
      sx={{
        width: { xs: 0, lg: isCollapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH_EXPANDED },
        flexShrink: 0,
        transition: 'width 200ms ease',
      }}
    >
      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onCloseMobile}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', lg: 'none' },
          '& .MuiDrawer-paper': {
            width: DRAWER_WIDTH_EXPANDED,
            boxSizing: 'border-box',
            backgroundColor: 'background.paper',
          },
        }}
      >
        {drawerContent}
      </Drawer>

      {/* Desktop Drawer */}
      <StyledDrawer
        variant="permanent"
        collapsed={isCollapsed ? 1 : 0}
        sx={{
          display: { xs: 'none', lg: 'block' },
        }}
      >
        {drawerContent}
      </StyledDrawer>
    </Box>
  );
};

export { DRAWER_WIDTH_EXPANDED, DRAWER_WIDTH_COLLAPSED };
export default Sidebar;
